import moment from 'moment-timezone';
import { TIMEZONE } from '../../lib/constants/bookingForm';

/**
 * Validates if a date is not in the past
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} todayDate - Today's date in YYYY-MM-DD format
 * @returns {string|null} Error message or null if valid
 */
export const validateDateNotInPast = (date, todayDate) => {
    const selectedDate = moment(date, 'YYYY-MM-DD', true);
    const today = moment().tz(TIMEZONE);
    
    if (!selectedDate.isValid()) {
        return 'Please enter a valid date.';
    }
    
    if (selectedDate.isBefore(today, 'day')) {
        return 'Please select a date from today onwards.';
    }
    
    return null;
};

/**
 * Validates if return date is after departure date
 * @param {string} returnDate - Return date in YYYY-MM-DD format
 * @param {string} departureDate - Departure date in YYYY-MM-DD format
 * @returns {string|null} Error message or null if valid
 */
export const validateReturnDateAfterDeparture = (returnDate, departureDate) => {
    const returnMoment = moment(returnDate, 'YYYY-MM-DD', true);
    const departureMoment = moment(departureDate, 'YYYY-MM-DD', true);
    
    if (returnMoment.isBefore(departureMoment, 'day')) {
        return 'Return date must be on or after departure date.';
    }
    
    return null;
};

/**
 * Validates if time is not in the past for today's date
 * @param {string} time - Time in hh:mm A format
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} todayDate - Today's date in YYYY-MM-DD format
 * @returns {string|null} Error message or null if valid
 */
export const validateTimeNotInPast = (time, date, todayDate) => {
    if (date === todayDate) {
        const currentTime = moment().tz(TIMEZONE);
        const time24h = moment(time, 'hh:mm A').format('HH:mm');
        const selectedDateTime = moment(date + ' ' + time24h, 'YYYY-MM-DD HH:mm');
        
        if (selectedDateTime.isBefore(currentTime)) {
            return 'Please select a time from now onwards for today.';
        }
    }
    
    return null;
};

/**
 * Validates if return time is after departure time for same day
 * @param {string} departureTime - Departure time in hh:mm A format
 * @param {string} returnTime - Return time in hh:mm A format
 * @param {string} departureDate - Departure date in YYYY-MM-DD format
 * @param {string} returnDate - Return date in YYYY-MM-DD format
 * @returns {boolean} True if return time is after departure time
 */
export const validateReturnTimeAfterDeparture = (departureTime, returnTime, departureDate, returnDate) => {
    const departureTime24h = moment(departureTime, 'hh:mm A').format('HH:mm');
    const returnTime24h = moment(returnTime, 'hh:mm A').format('HH:mm');
    
    const format = 'YYYY-MM-DD HH:mm';
    const departure = moment(departureDate + ' ' + departureTime24h, format).unix();
    const returnMoment = moment(returnDate + ' ' + returnTime24h, format).unix();

    return returnMoment > departure;
};

/**
 * Validates required field
 * @param {string} value - Field value
 * @param {string} fieldName - Field name for error message
 * @returns {string|null} Error message or null if valid
 */
export const validateRequired = (value, fieldName) => {
    if (!value || !value.trim()) {
        return `Please ${fieldName === 'pickUpAddress' ? 'select pickup location' : 
                fieldName === 'dropOffAddress' ? 'select dropoff location' :
                fieldName === 'tripType' ? 'select a trip type' :
                fieldName === 'noOfPassengers' ? 'enter number of passengers' :
                fieldName === 'departureDate' ? 'select departure date' :
                fieldName === 'departureTime' ? 'select departure time' :
                fieldName === 'returnDate' ? 'select return date' :
                fieldName === 'returnTime' ? 'select return time' :
                `enter ${fieldName}`}.`;
    }
    return null;
};

/**
 * Validates number of passengers
 * @param {string} value - Number of passengers
 * @returns {string|null} Error message or null if valid
 */
export const validatePassengers = (value) => {
    if (!value) {
        return 'Please enter number of passengers.';
    }
    
    const numPassengers = parseInt(value);
    if (numPassengers < 1) {
        return 'Please enter at least 1 passenger.';
    }
    
    return null;
};
